import React, { useEffect, useState, useMemo, useRef } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import LinkedCorrespondenceCard from '../../components/LinkedCorrespondenceCard';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import TableEmptyRow from '../../components/TableEmptyRow';
import TablePagination from '../../components/TablePagination';
import StatusBadge from '../../components/StatusBadge';
import { printTableElement } from '../../utils/exportUtils';
import { drawingsAPI, milestonesAPI, adminAPI } from '../../services/authService';
import { toStoredAttachments, downloadAttachment, viewAttachmentInNewTab } from '../../utils/attachmentStorage';
import { notifyError, notifySuccess, notifyWarning } from '../../utils/notify';
import { confirmDialog } from '../../utils/confirmDialog';
import { formatDateIN } from '../../utils/dateFormat';
import TabbedSection from '../../components/TabbedSection';
import { useFormConfiguration } from '../../hooks/useFormConfiguration';
import { ConfiguredField } from '../../components/ConfiguredField';
import { DynamicFieldsSection } from '../../components/DynamicFieldRenderer';

const DISCIPLINES = [
  'Architectural', 'Structural', 'MEP', 'Electrical', 'Plumbing', 'Fire Fighting',
  'HVAC', 'ELV', 'Landscape', 'Interior', 'Infra / Roads', 'Composite / General',
];

const DRAWING_TYPES = [
  'Concept', 'GA Drawing', 'Working Drawing', 'Shop Drawing', 'Coordination Drawing',
  'BBS', 'As-Built Drawing', 'Temporary Works Drawing', 'Design Calculation', 'Sketch / Detail',
];

const STAGES = [
  'Draft', 'Submitted', 'Under Review', 'Approved', 'Approved with Comments',
  'Resubmit', 'Rejected', 'Superseded', 'As-Built',
];

const SITE_IMPACT_LEVELS = ['None', 'Low', 'Medium', 'High', 'Blocking'];

const STAGE_TONE_MAP = {
  Draft: 'draft',
  Submitted: 'open',
  'Under Review': 'under-review',
  Approved: 'approved',
  'Approved with Comments': 'medium',
  Resubmit: 'critical',
  Rejected: 'critical',
  Superseded: 'closed',
  'As-Built': 'approved',
};

const normalizeRow = (row) => {
  const d = row.data || {};
  return {
    id: row.id,
    project_id: row.projectId ?? row.project_id ?? null,
    drawing_no: row.drawingNo ?? row.drawing_no ?? d.drawing_no ?? `DWG-${row.id}`,
    title: row.title ?? d.drawing_title ?? '',
    status: row.status ?? d.stage ?? 'Draft',
    discipline: d.discipline ?? '',
    drawing_type: d.drawing_type ?? '',
    sheet_no: d.sheet_no ?? '',
    revision_no: d.revision_no ?? '',
    revision_date: d.revision_date ?? null,
    stage: d.stage ?? row.status ?? 'Draft',
    submission_date: d.submission_date ?? null,
    approval_date: d.approval_date ?? null,
    required_by_date: d.required_by_date ?? null,
    submitted_by: d.submitted_by ?? '',
    submitted_to: d.submitted_to ?? '',
    received_from: d.received_from ?? '',
    consultant_name: d.consultant_name ?? '',
    vendor_name: d.vendor_name ?? '',
    latest_revision_flag: d.latest_revision_flag ?? false,
    site_use_flag: d.site_use_flag ?? false,
    construction_hold_flag: d.construction_hold_flag ?? false,
    site_impact_level: d.site_impact_level ?? 'None',
    primary_correspondence_chain_id: d.primary_correspondence_chain_id ?? d.linked_correspondence ?? null,
    remarks: d.remarks ?? '',
    change_summary: d.change_summary ?? '',
    attachments: d.attachments ?? [],
    design_status: d.design_status ?? '',
    issue_status: d.issue_status ?? '',
    linked_milestone_id: d.linked_milestone_id ?? null,
    delay_risk_flag: d.delay_risk_flag ?? false,
    eot_relevance_flag: d.eot_relevance_flag ?? false,
    supersedes_drawing_id: d.supersedes_drawing_id ?? null,
    superseded_by_drawing_id: d.superseded_by_drawing_id ?? null,
  };
};

const DrawingsDesign = ({ onViewThread }) => {
  const [projectFilter, setProjectFilter] = useState('all');
  const [disciplineFilter, setDisciplineFilter] = useState('all');
  const [drawingTypeFilter, setDrawingTypeFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [latestOnly, setLatestOnly] = useState(false);
  const [pendingApprovalOnly, setPendingApprovalOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [constructionHoldOnly, setConstructionHoldOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [detailTab, setDetailTab] = useState('summary');
  const [showModal, setShowModal] = useState(false);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const { projects: projectOptions } = useProjects();
  const [dataReady, setDataReady] = useState(false);
  const [drawings, setDrawings] = useState([]);
  const [formData, setFormData] = useState({
    project_id: '',
    discipline: 'Architectural',
    drawing_type: 'Working Drawing',
    drawing_no: '',
    drawing_title: '',
    sheet_no: '',
    revision_no: '',
    revision_date: '',
    stage: 'Draft',
    submitted_by: '',
    submitted_to: '',
    received_from: '',
    consultant_name: '',
    vendor_name: '',
    submission_date: '',
    required_by_date: '',
    approval_date: '',
    site_use_flag: false,
    construction_hold_flag: false,
    site_impact_level: 'None',
    delay_risk_flag: false,
    eot_relevance_flag: false,
    linked_milestone_id: '',
    remarks: '',
    change_summary: '',
    attachments: [],
  });
  const [editingId, setEditingId] = useState(null);
  const perPage = 10;
  const tableRef = useRef(null);
  const [projectMilestones, setProjectMilestones] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const formConfig = useFormConfiguration({ pageKey: 'drawings_design', formKey: 'drawing_create_edit', mode: editingId ? 'edit' : 'create' });
  const customFields = formConfig.customFields || [];

  const defaultsFromCustomFields = () => {
    const out = {};
    for (const f of customFields) {
      if (f.defaultValue != null && f.defaultValue !== '') out[f.fieldKey] = f.defaultValue;
      else if (f.fieldType === 'boolean') out[f.fieldKey] = false;
      else out[f.fieldKey] = '';
    }
    return out;
  };

  const onCustomChange = (fieldKey, value) => {
    setCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await drawingsAPI.getAll({ page: 1, pageSize: 500 });
        if (cancelled) return;
        const { rows: rawRows } = unwrapListResponse(res);
        const rows = safeArray(rawRows).map(normalizeRow);
        setDrawings(rows);
      } catch (e) {
        if (!cancelled) setDrawings([]);
      } finally {
        if (!cancelled) setDataReady(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const projects = safeArray(projectOptions);

  // Load milestones for the current project (for linkage in form + detail)
  useEffect(() => {
    const currentProjectId = formData.project_id || selectedDrawing?.project_id;
    if (!currentProjectId) {
      setProjectMilestones([]);
      return;
    }
    let cancelled = false;
    const loadMilestones = async () => {
      try {
        const resp = await milestonesAPI.getAll({ page: 1, pageSize: 200, projectId: currentProjectId });
        if (cancelled) return;
        const { rows: rawRows } = unwrapListResponse(resp);
        const rows = safeArray(rawRows).map((row) => {
          const payload = row.data || {};
          return {
            id: row.id,
            project_id: row.projectId ?? row.project_id ?? Number(currentProjectId),
            name: payload.name ?? row.title ?? row.name ?? `Milestone ${row.id}`,
            due_date: payload.revised_date ?? payload.baseline_date ?? row.dueDate ?? null,
            status: row.status || payload.status || 'On Track',
          };
        });
        setProjectMilestones(rows);
      } catch {
        if (!cancelled) setProjectMilestones([]);
      }
    };
    loadMilestones();
    return () => { cancelled = true; };
  }, [formData.project_id, selectedDrawing?.project_id]);

  const filteredDrawings = useMemo(() => {
    let list = [...drawings];
    if (projectFilter !== 'all') list = list.filter((d) => d.project_id === parseInt(projectFilter, 10));
    if (disciplineFilter !== 'all') list = list.filter((d) => (d.discipline || '') === disciplineFilter);
    if (drawingTypeFilter !== 'all') list = list.filter((d) => (d.drawing_type || '') === drawingTypeFilter);
    if (stageFilter !== 'all') list = list.filter((d) => (d.stage || d.status || '') === stageFilter);
    if (latestOnly) list = list.filter((d) => d.latest_revision_flag === true);
    if (pendingApprovalOnly) list = list.filter((d) => ['Submitted', 'Under Review'].includes(d.stage || d.status));
    if (overdueOnly) list = list.filter((d) => {
      const req = d.required_by_date;
      if (!req) return false;
      return new Date(req) < new Date() && !d.approval_date && ['Submitted', 'Under Review'].includes(d.stage || d.status);
    });
    if (constructionHoldOnly) list = list.filter((d) => d.construction_hold_flag === true);
    return list;
  }, [drawings, projectFilter, disciplineFilter, drawingTypeFilter, stageFilter, latestOnly, pendingApprovalOnly, overdueOnly, constructionHoldOnly]);

  const totalPages = Math.max(1, Math.ceil(filteredDrawings.length / perPage));
  const paginatedDrawings = filteredDrawings.slice((page - 1) * perPage, page * perPage);
  const showProjectColumn = projectFilter === 'all';

  const handleCreateOrUpdate = async () => {
    if (isSubmitting) return;
    if (!formData.project_id || !formData.drawing_no?.trim() || !formData.drawing_title?.trim() || !formData.discipline || !formData.drawing_type || !formData.revision_no?.trim()) {
      notifyWarning('Project, Drawing No, Title, Discipline, Drawing Type and Revision No are required.');
      return;
    }
    if (formConfig.customFieldsEnabled) {
      for (const f of customFields) {
        if (!f.required || f.hidden) continue;
        const v = customValues[f.fieldKey];
        if (v === undefined || v === null || v === '') {
          notifyError(`${f.label || f.fieldKey} is required.`);
          return;
        }
      }
    }
    const payload = {
      projectId: Number(formData.project_id),
      drawingNo: formData.drawing_no.trim(),
      title: formData.drawing_title.trim(),
      status: formData.stage || 'Draft',
      data: {
        discipline: formData.discipline,
        drawing_type: formData.drawing_type,
        drawing_no: formData.drawing_no.trim(),
        drawing_title: formData.drawing_title.trim(),
        sheet_no: formData.sheet_no || null,
        revision_no: formData.revision_no,
        revision_date: formData.revision_date || null,
        stage: formData.stage,
        submitted_by: formData.submitted_by || null,
        submitted_to: formData.submitted_to || null,
        received_from: formData.received_from || null,
        consultant_name: formData.consultant_name || null,
        vendor_name: formData.vendor_name || null,
        submission_date: formData.submission_date || null,
        required_by_date: formData.required_by_date || null,
        approval_date: formData.approval_date || null,
        site_use_flag: formData.site_use_flag,
        construction_hold_flag: formData.construction_hold_flag,
        site_impact_level: formData.site_impact_level || 'None',
        delay_risk_flag: formData.delay_risk_flag,
        eot_relevance_flag: formData.eot_relevance_flag,
        linked_milestone_id: formData.linked_milestone_id ? Number(formData.linked_milestone_id) : null,
        remarks: formData.remarks || null,
        change_summary: formData.change_summary || null,
        attachments: formData.attachments || [],
      },
    };
    if (editingId) {
      try {
        const res = await drawingsAPI.update(editingId, payload);
        const row = res?.data;
        if (row) {
          const normalized = normalizeRow(row);
          setDrawings((prev) => prev.map((d) => (d.id === editingId ? normalized : d)));
          setSelectedDrawing((prev) => (prev?.id === editingId ? normalized : prev));
        }
        if (formConfig.customFieldsEnabled && row?.id) {
          try {
            await adminAPI.saveCustomFieldValues('Drawing', row.id, {
              pageKey: 'drawings_design',
              projectId: Number(formData.project_id),
              values: customValues,
              mode: 'edit',
            });
          } catch (cvErr) {
            console.error('Custom field save failed:', cvErr);
            notifyError(cvErr?.response?.data?.message || 'Drawing saved but custom fields failed.');
          }
        }
        setShowCreateModal(false);
        setEditingId(null);
        setCustomValues({});
        notifySuccess('Drawing updated.');
      } catch (e) {
        notifyError('Update failed.');
      }
    } else {
      if (!(await confirmDialog('Create this drawing record?', 'Drawings & Design'))) return;
      setIsSubmitting(true);
      try {
        const res = await drawingsAPI.create(payload);
        const row = res?.data;
        if (row) setDrawings((prev) => [normalizeRow(row), ...prev]);
        if (formConfig.customFieldsEnabled && row?.id) {
          try {
            await adminAPI.saveCustomFieldValues('Drawing', row.id, {
              pageKey: 'drawings_design',
              projectId: Number(formData.project_id),
              values: customValues,
              mode: 'create',
            });
          } catch (cvErr) {
            console.error('Custom field save failed:', cvErr);
            notifyError(cvErr?.response?.data?.message || 'Drawing saved but custom fields failed.');
          }
        }
        setShowCreateModal(false);
        setFormData({ ...formData, project_id: '', drawing_no: '', drawing_title: '', revision_no: '', sheet_no: '', revision_date: '', submission_date: '', required_by_date: '', approval_date: '', remarks: '', change_summary: '', attachments: [] });
        setCustomValues({});
        notifySuccess('Drawing registered.');
      } catch (e) {
        notifyError('Create failed.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const openEdit = async (d) => {
    setEditingId(d.id);
    setCustomValues(defaultsFromCustomFields());
    if (formConfig.customFieldsEnabled) {
      try {
        const res = await adminAPI.getCustomFieldValues('Drawing', d.id);
        setCustomValues({
          ...defaultsFromCustomFields(),
          ...(res?.data?.data?.values || res?.data?.values || {}),
        });
      } catch (_e) {
        /* keep defaults */
      }
    }
    setFormData({
      project_id: d.project_id ?? '',
      discipline: d.discipline || 'Architectural',
      drawing_type: d.drawing_type || 'Working Drawing',
      drawing_no: d.drawing_no ?? '',
      drawing_title: d.title ?? '',
      sheet_no: d.sheet_no ?? '',
      revision_no: d.revision_no ?? '',
      revision_date: d.revision_date ? (typeof d.revision_date === 'string' ? d.revision_date.slice(0, 10) : '') : '',
      stage: d.stage || d.status || 'Draft',
      submitted_by: d.submitted_by ?? '',
      submitted_to: d.submitted_to ?? '',
      received_from: d.received_from ?? '',
      consultant_name: d.consultant_name ?? '',
      vendor_name: d.vendor_name ?? '',
      submission_date: d.submission_date ? (typeof d.submission_date === 'string' ? d.submission_date.slice(0, 10) : new Date(d.submission_date).toISOString().slice(0, 10)) : '',
      required_by_date: d.required_by_date ? (typeof d.required_by_date === 'string' ? d.required_by_date.slice(0, 10) : new Date(d.required_by_date).toISOString().slice(0, 10)) : '',
      approval_date: d.approval_date ? (typeof d.approval_date === 'string' ? d.approval_date.slice(0, 10) : new Date(d.approval_date).toISOString().slice(0, 10)) : '',
      site_use_flag: !!d.site_use_flag,
      construction_hold_flag: !!d.construction_hold_flag,
      site_impact_level: d.site_impact_level || 'None',
      delay_risk_flag: !!d.delay_risk_flag,
      eot_relevance_flag: !!d.eot_relevance_flag,
      linked_milestone_id: d.linked_milestone_id ?? '',
      remarks: d.remarks ?? '',
      change_summary: d.change_summary ?? '',
      attachments: d.attachments ?? [],
    });
    setShowCreateModal(true);
  };

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setAttachmentUploading(true);
    toStoredAttachments(files).then((stored) => {
      setFormData((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...stored] }));
    }).finally(() => setAttachmentUploading(false));
    e.target.value = '';
  };

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>Drawings & Design Control</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }

  const projectName = projectFilter === 'all' ? 'All Projects' : (projects.find((p) => p.id === parseInt(projectFilter, 10))?.name || 'Selected Project');

  return (
    <div>
      <div className="section-header">
        <h2>Drawings & Design Control <span className="count-badge">{filteredDrawings.length}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary"
            onClick={() => printTableElement({ title: 'Drawings & Design Register', subtitle: `Project: ${projectName}`, element: tableRef.current })}
          >
            Download PDF
          </button>
          <button
            className="btn-primary"
            onClick={() => { setEditingId(null); setFormData({ ...formData, project_id: '', drawing_no: '', drawing_title: '', revision_no: '', sheet_no: '', revision_date: '', submission_date: '', required_by_date: '', approval_date: '', remarks: '', change_summary: '', attachments: [] }); setCustomValues(defaultsFromCustomFields()); setShowCreateModal(true); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            New Drawing
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect value={projectFilter} onChange={(v) => { setProjectFilter(v); setPage(1); }} projects={projects} />
        <select value={disciplineFilter} onChange={(e) => { setDisciplineFilter(e.target.value); setPage(1); }}>
          <option value="all">All Disciplines</option>
          {DISCIPLINES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select value={drawingTypeFilter} onChange={(e) => { setDrawingTypeFilter(e.target.value); setPage(1); }}>
          <option value="all">All Types</option>
          {DRAWING_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select value={stageFilter} onChange={(e) => { setStageFilter(e.target.value); setPage(1); }}>
          <option value="all">All Stages</option>
          {STAGES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={latestOnly} onChange={(e) => { setLatestOnly(e.target.checked); setPage(1); }} />
          <span style={{ fontSize: 13 }}>Latest only</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={pendingApprovalOnly} onChange={(e) => { setPendingApprovalOnly(e.target.checked); setPage(1); }} />
          <span style={{ fontSize: 13 }}>Pending approval</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={overdueOnly} onChange={(e) => { setOverdueOnly(e.target.checked); setPage(1); }} />
          <span style={{ fontSize: 13 }}>Overdue</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={constructionHoldOnly} onChange={(e) => { setConstructionHoldOnly(e.target.checked); setPage(1); }} />
          <span style={{ fontSize: 13 }}>Construction hold</span>
        </label>
        <ResetFiltersButton
          onClick={() => {
            setProjectFilter('all');
            setDisciplineFilter('all');
            setDrawingTypeFilter('all');
            setStageFilter('all');
            setLatestOnly(false);
            setPendingApprovalOnly(false);
            setOverdueOnly(false);
            setConstructionHoldOnly(false);
            setPage(1);
          }}
        />
      </div>

      <div className="table-container" ref={tableRef}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Drawing No</th>
              <th>Title</th>
              {showProjectColumn && <th>Project</th>}
              <th>Discipline</th>
              <th>Type</th>
              <th>Revision</th>
              <th>Stage</th>
              <th>Submission</th>
              <th>Approval</th>
              <th>Required By</th>
              <th>Consultant / Vendor</th>
              <th>Site Impact</th>
              <th>Latest</th>
              <th>Hold</th>
              <th>Linked Corr.</th>
            </tr>
          </thead>
          <tbody>
            {paginatedDrawings.length === 0 ? (
              <TableEmptyRow colSpan={14} message="No drawings found." />
            ) : (
              paginatedDrawings.map((d) => {
                const consultantVendor = [d.consultant_name, d.vendor_name].filter(Boolean).join(' / ') || '-';
                const linkedId = d.primary_correspondence_chain_id;
                return (
                  <tr key={d.id} className="clickable" onClick={() => { setSelectedDrawing(d); setDetailTab('summary'); setShowModal(true); }}>
                    <td style={{ fontWeight: 500, color: '#7C3AED' }}>{d.drawing_no}</td>
                    <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.title}>{d.title || '-'}</td>
                    {showProjectColumn && <td style={{ fontSize: 12, color: '#6b7280' }}>{projects.find((p) => p.id === d.project_id)?.name ?? '-'}</td>}
                    <td>{d.discipline || '-'}</td>
                    <td>{d.drawing_type || '-'}</td>
                    <td>{d.revision_no || '-'}</td>
                    <td><StatusBadge tone={STAGE_TONE_MAP[d.stage] || 'draft'}>{d.stage || d.status}</StatusBadge></td>
                    <td>{formatDateIN(d.submission_date)}</td>
                    <td>{formatDateIN(d.approval_date)}</td>
                    <td>{formatDateIN(d.required_by_date)}</td>
                    <td style={{ fontSize: 12 }}>{consultantVendor}</td>
                    <td>{d.site_impact_level || 'None'}</td>
                    <td>{d.latest_revision_flag ? 'Yes' : '-'}</td>
                    <td>{d.construction_hold_flag ? 'Yes' : '-'}</td>
                    <td>
                      {linkedId ? (
                        <button type="button" className="btn-secondary btn-sm" onClick={(ev) => { ev.stopPropagation(); onViewThread?.(linkedId); }}>View</button>
                      ) : '-'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <TablePagination page={page} totalPages={totalPages} onPrevious={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(totalPages, p + 1))} />
      </div>

      {selectedDrawing && showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 720 }}>
            <div className="modal-header">
              <h2>Drawing — {selectedDrawing.drawing_no} (Rev {selectedDrawing.revision_no || '-'})</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-secondary btn-sm" onClick={() => openEdit(selectedDrawing)}>Edit</button>
                <button className="btn-close" onClick={() => { setSelectedDrawing(null); setShowModal(false); }}>
                  <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                </button>
              </div>
            </div>
            <div className="modal-body">
              <TabbedSection
                activeKey={detailTab}
                onChange={setDetailTab}
                tabs={[
                  {
                    key: 'summary',
                    label: 'Summary',
                    content: (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Drawing No</p><p style={{ fontWeight: 500 }}>{selectedDrawing.drawing_no}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Title</p><p>{selectedDrawing.title || '-'}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Discipline</p><p>{selectedDrawing.discipline || '-'}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Drawing Type</p><p>{selectedDrawing.drawing_type || '-'}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Sheet No</p><p>{selectedDrawing.sheet_no || '-'}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Revision No / Date</p><p>{selectedDrawing.revision_no || '-'} / {formatDateIN(selectedDrawing.revision_date)}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Stage</p><p><StatusBadge tone={STAGE_TONE_MAP[selectedDrawing.stage] || 'draft'}>{selectedDrawing.stage || selectedDrawing.status}</StatusBadge></p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Consultant / Vendor</p><p>{[selectedDrawing.consultant_name, selectedDrawing.vendor_name].filter(Boolean).join(' / ') || '-'}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Latest Revision</p><p>{selectedDrawing.latest_revision_flag ? 'Yes' : 'No'}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Site Use</p><p>{selectedDrawing.site_use_flag ? 'Yes' : 'No'}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Construction Hold</p><p>{selectedDrawing.construction_hold_flag ? 'Yes' : 'No'}</p></div>
                        <div style={{ gridColumn: '1 / -1' }}><p style={{ fontSize: 12, color: '#6b7280' }}>Remarks</p><p>{selectedDrawing.remarks || '-'}</p></div>
                      </div>
                    ),
                  },
                  {
                    key: 'revisions',
                    label: 'Revisions',
                    content: (
                      <div>
                        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Current revision: {selectedDrawing.revision_no || '-'} ({formatDateIN(selectedDrawing.revision_date)})</p>
                        {selectedDrawing.change_summary && <p><strong>Change summary:</strong> {selectedDrawing.change_summary}</p>}
                        <p style={{ fontSize: 12, color: '#6b7280' }}>Revision history is shown here. Supersedes / superseded-by linkage can be extended when revision child records are added.</p>
                      </div>
                    ),
                  },
                  {
                    key: 'approval',
                    label: 'Approval / Review',
                    content: (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Submission Date</p><p>{formatDateIN(selectedDrawing.submission_date)}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Required By</p><p>{formatDateIN(selectedDrawing.required_by_date)}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Approval Date</p><p>{formatDateIN(selectedDrawing.approval_date)}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Stage</p><p><StatusBadge tone={STAGE_TONE_MAP[selectedDrawing.stage] || 'draft'}>{selectedDrawing.stage || selectedDrawing.status}</StatusBadge></p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Submitted By</p><p>{selectedDrawing.submitted_by || '-'}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Submitted To</p><p>{selectedDrawing.submitted_to || '-'}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Received From</p><p>{selectedDrawing.received_from || '-'}</p></div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          {selectedDrawing.required_by_date && !selectedDrawing.approval_date && new Date(selectedDrawing.required_by_date) < new Date() && (
                            <span className="status-badge critical">Overdue Response</span>
                          )}
                          {['Submitted', 'Under Review'].includes(selectedDrawing.stage || selectedDrawing.status) && (
                            <span className="status-badge open" style={{ marginLeft: 8 }}>Pending Approval</span>
                          )}
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'site-impact',
                    label: 'Site Impact',
                    content: (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Site Impact Level</p><p>{selectedDrawing.site_impact_level || 'None'}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Construction Hold</p><p>{selectedDrawing.construction_hold_flag ? 'Yes' : 'No'}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>Delay Risk</p><p>{selectedDrawing.delay_risk_flag ? 'Yes' : 'No'}</p></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280' }}>EOT Relevance</p><p>{selectedDrawing.eot_relevance_flag ? 'Yes' : 'No'}</p></div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <p style={{ fontSize: 12, color: '#6b7280' }}>Linked Milestone / EOT Request</p>
                          <p>
                            {(() => {
                              if (!selectedDrawing.linked_milestone_id) return '-';
                              const ms = projectMilestones.find((m) => m.id === selectedDrawing.linked_milestone_id);
                              if (!ms) return `ID: ${selectedDrawing.linked_milestone_id}`;
                              return `${ms.name} (${ms.status}${ms.due_date ? ` · ${formatDateIN(ms.due_date)}` : ''})`;
                            })()}
                          </p>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          {selectedDrawing.construction_hold_flag && <p style={{ color: '#b91c1c', fontWeight: 500 }}>This drawing is holding execution.</p>}
                          {selectedDrawing.delay_risk_flag && <p style={{ color: '#b45309' }}>This drawing may affect milestone progress.</p>}
                          {selectedDrawing.eot_relevance_flag && <p style={{ color: '#047857' }}>This drawing may be relevant for EOT justification.</p>}
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'linked',
                    label: 'Linked Correspondence',
                    content: (
                      <div>
                        <LinkedCorrespondenceCard linkedCorrespondenceId={selectedDrawing.primary_correspondence_chain_id} />
                        {selectedDrawing.primary_correspondence_chain_id && onViewThread && (
                          <button type="button" className="btn-secondary" style={{ marginTop: 8 }} onClick={() => onViewThread(selectedDrawing.primary_correspondence_chain_id)}>Open Subject</button>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'attachments',
                    label: 'Attachments',
                    content: (
                      <div>
                        {(selectedDrawing.attachments || []).length === 0 ? (
                          <p style={{ fontSize: 13, color: '#6b7280' }}>No attachments.</p>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {selectedDrawing.attachments.map((file, idx) => (
                              <span key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <span style={{ fontSize: 13 }}>{file?.name || `File ${idx + 1}`}</span>
                                <button type="button" className="btn-secondary btn-sm" onClick={() => viewAttachmentInNewTab(file, () => downloadAttachment(file, file?.name))}>View</button>
                                <button type="button" className="btn-secondary btn-sm" onClick={() => downloadAttachment(file, file?.name)}>Download</button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Drawing' : 'New Drawing'}</h2>
              <button className="btn-close" onClick={() => { setShowCreateModal(false); setEditingId(null); }}>
                <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
            </div>
            <div className="modal-body">
              {!formConfig.fallback && (
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 0 }}>Using published form configuration v{formConfig.config?.versionNumber || '—'}</p>
              )}
              <div className="form-row">
                <ConfiguredField fieldKey="project_id" formConfig={formConfig} defaultLabel="Project" defaultRequired>
                  {({ readOnly }) => (
                    <select value={formData.project_id} disabled={readOnly} onChange={(e) => setFormData((p) => ({ ...p, project_id: e.target.value }))}><option value="">Select</option>{projects.filter((p) => p.status === 'Active').map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
                  )}
                </ConfiguredField>
                <ConfiguredField fieldKey="discipline" formConfig={formConfig} defaultLabel="Discipline" defaultRequired>
                  {({ readOnly }) => (
                    <select value={formData.discipline} disabled={readOnly} onChange={(e) => setFormData((p) => ({ ...p, discipline: e.target.value }))}>{DISCIPLINES.map((x) => <option key={x} value={x}>{x}</option>)}</select>
                  )}
                </ConfiguredField>
              </div>
              <div className="form-row">
                <ConfiguredField fieldKey="drawing_type" formConfig={formConfig} defaultLabel="Drawing Type" defaultRequired>
                  {({ readOnly }) => (
                    <select value={formData.drawing_type} disabled={readOnly} onChange={(e) => setFormData((p) => ({ ...p, drawing_type: e.target.value }))}>{DRAWING_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}</select>
                  )}
                </ConfiguredField>
                <ConfiguredField fieldKey="stage" formConfig={formConfig} defaultLabel="Stage">
                  {({ readOnly }) => (
                    <select value={formData.stage} disabled={readOnly} onChange={(e) => setFormData((p) => ({ ...p, stage: e.target.value }))}>{STAGES.map((x) => <option key={x} value={x}>{x}</option>)}</select>
                  )}
                </ConfiguredField>
              </div>
              <div className="form-row">
                <ConfiguredField fieldKey="drawing_no" formConfig={formConfig} defaultLabel="Drawing No" defaultRequired>
                  {({ readOnly, placeholder }) => (
                    <input type="text" value={formData.drawing_no} disabled={readOnly} onChange={(e) => setFormData((p) => ({ ...p, drawing_no: e.target.value }))} placeholder={placeholder || 'e.g. A-101'} />
                  )}
                </ConfiguredField>
                <ConfiguredField fieldKey="drawing_title" formConfig={formConfig} defaultLabel="Drawing Title" defaultRequired>
                  {({ readOnly, placeholder }) => (
                    <input type="text" value={formData.drawing_title} disabled={readOnly} onChange={(e) => setFormData((p) => ({ ...p, drawing_title: e.target.value }))} placeholder={placeholder || 'Title'} />
                  )}
                </ConfiguredField>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Sheet No</label><input type="text" value={formData.sheet_no} onChange={(e) => setFormData((p) => ({ ...p, sheet_no: e.target.value }))} /></div>
                <ConfiguredField fieldKey="revision_no" formConfig={formConfig} defaultLabel="Revision No" defaultRequired>
                  {({ readOnly, placeholder }) => (
                    <input type="text" value={formData.revision_no} disabled={readOnly} onChange={(e) => setFormData((p) => ({ ...p, revision_no: e.target.value }))} placeholder={placeholder || '0, A, B...'} />
                  )}
                </ConfiguredField>
                <div className="form-group"><label>Revision Date</label><input type="date" value={formData.revision_date} onChange={(e) => setFormData((p) => ({ ...p, revision_date: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Submitted By</label><input type="text" value={formData.submitted_by} onChange={(e) => setFormData((p) => ({ ...p, submitted_by: e.target.value }))} /></div>
                <div className="form-group"><label>Submitted To</label><input type="text" value={formData.submitted_to} onChange={(e) => setFormData((p) => ({ ...p, submitted_to: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Received From</label><input type="text" value={formData.received_from} onChange={(e) => setFormData((p) => ({ ...p, received_from: e.target.value }))} /></div>
                <div className="form-group"><label>Consultant Name</label><input type="text" value={formData.consultant_name} onChange={(e) => setFormData((p) => ({ ...p, consultant_name: e.target.value }))} /></div>
                <div className="form-group"><label>Vendor Name</label><input type="text" value={formData.vendor_name} onChange={(e) => setFormData((p) => ({ ...p, vendor_name: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Submission Date</label><input type="date" value={formData.submission_date} onChange={(e) => setFormData((p) => ({ ...p, submission_date: e.target.value }))} /></div>
                <div className="form-group"><label>Required By Date</label><input type="date" value={formData.required_by_date} onChange={(e) => setFormData((p) => ({ ...p, required_by_date: e.target.value }))} /></div>
                <div className="form-group"><label>Approval Date</label><input type="date" value={formData.approval_date} onChange={(e) => setFormData((p) => ({ ...p, approval_date: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Site Impact Level</label>
                  <select value={formData.site_impact_level} onChange={(e) => setFormData((p) => ({ ...p, site_impact_level: e.target.value }))}>
                    {SITE_IMPACT_LEVELS.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Linked Milestone / EOT</label>
                  <select
                    value={formData.linked_milestone_id}
                    onChange={(e) => setFormData((p) => ({ ...p, linked_milestone_id: e.target.value }))}
                    disabled={!formData.project_id}
                  >
                    <option value="">None</option>
                    {projectMilestones
                      .filter((m) => !formData.project_id || String(m.project_id) === String(formData.project_id))
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}{m.due_date ? ` · ${formatDateIN(m.due_date)}` : ''}
                        </option>
                      ))}
                  </select>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={formData.site_use_flag} onChange={(e) => setFormData((p) => ({ ...p, site_use_flag: e.target.checked }))} /> Site use
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={formData.construction_hold_flag} onChange={(e) => setFormData((p) => ({ ...p, construction_hold_flag: e.target.checked }))} /> Construction hold
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={formData.delay_risk_flag} onChange={(e) => setFormData((p) => ({ ...p, delay_risk_flag: e.target.checked }))} /> Delay risk
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={formData.eot_relevance_flag} onChange={(e) => setFormData((p) => ({ ...p, eot_relevance_flag: e.target.checked }))} /> EOT relevance
                </label>
              </div>
              <ConfiguredField fieldKey="remarks" formConfig={formConfig} defaultLabel="Remarks">
                {({ readOnly, placeholder }) => (
                  <textarea value={formData.remarks} disabled={readOnly} placeholder={placeholder} onChange={(e) => setFormData((p) => ({ ...p, remarks: e.target.value }))} rows={2} />
                )}
              </ConfiguredField>
              <div className="form-group"><label>Change Summary</label><textarea value={formData.change_summary} onChange={(e) => setFormData((p) => ({ ...p, change_summary: e.target.value }))} rows={2} /></div>
              {formConfig.customFieldsEnabled && (
                <DynamicFieldsSection
                  fields={customFields}
                  values={customValues}
                  onChange={onCustomChange}
                  title="Additional fields"
                />
              )}
              {!formConfig.isHidden('attachments') && (
              <div className="form-group">
                <label>{formConfig.labelFor('attachments', 'Attachments')}</label>
                <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleAttachmentChange} disabled={attachmentUploading} />
                {formData.attachments?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {formData.attachments.map((f, i) => (
                      <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '4px 8px' }}>
                        {f?.name}<button type="button" className="btn-secondary btn-sm" onClick={() => setFormData((p) => ({ ...p, attachments: (p.attachments || []).filter((_, idx) => idx !== i) }))}>✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              )}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => { setShowCreateModal(false); setEditingId(null); }} disabled={isSubmitting}>Cancel</button>
                <button className="btn-primary" onClick={handleCreateOrUpdate} disabled={isSubmitting}>{editingId ? 'Update' : (isSubmitting ? 'Creating...' : 'Create')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawingsDesign;

